import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Stack,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { Icon } from "@iconify/react";

const isMinor = (birthdate) => {
  if (!birthdate) return false;
  const age = (new Date() - new Date(birthdate)) / (1000 * 60 * 60 * 24 * 365.25);
  return age < 18;
};

const getParticipantKey = (participant, idx) =>
  String(participant?._key || participant?.memberRef || idx);

const getFieldRefKey = ({ scope, fieldId, participantKey }) =>
  scope === "booking"
    ? `booking:${fieldId}`
    : `participant:${participantKey}:${fieldId}`;

function renderCustomField(field, value, onChange, errorText = "") {
  const hasError = Boolean(errorText);

  switch (field.type) {
    case "text":
      return (
        <TextField
          key={field.fieldId}
          label={field.label}
          placeholder={field.placeholder}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
          error={hasError}
          helperText={errorText}
          size="small"
          fullWidth
        />
      );
    case "number":
      return (
        <TextField
          key={field.fieldId}
          label={field.label}
          placeholder={field.placeholder}
          type="number"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
          error={hasError}
          helperText={errorText}
          size="small"
          fullWidth
        />
      );
    case "date":
      return (
        <TextField
          key={field.fieldId}
          label={field.label}
          type="date"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
          error={hasError}
          helperText={errorText}
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
      );
    case "checkbox":
      return (
        <FormControl
          key={field.fieldId}
          required={field.required}
          error={hasError}
          component="fieldset"
          variant="standard"
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={!!value}
                onChange={(e) => onChange(e.target.checked)}
                required={field.required}
              />
            }
            label={field.label}
          />
          {hasError && <FormHelperText>{errorText}</FormHelperText>}
        </FormControl>
      );
    case "select":
      return (
        <FormControl
          key={field.fieldId}
          size="small"
          fullWidth
          required={field.required}
          error={hasError}
        >
          <InputLabel>{field.label}</InputLabel>
          <Select
            value={value || ""}
            label={field.label}
            onChange={(e) => onChange(e.target.value)}
          >
            <MenuItem value="">—</MenuItem>
            {(field.options || []).map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
          {hasError && <FormHelperText>{errorText}</FormHelperText>}
        </FormControl>
      );
    case "multiselect":
      return (
        <FormControl key={field.fieldId} required={field.required} error={hasError} component="fieldset">
          <Typography variant="body2" gutterBottom>{field.label}{field.required && " *"}</Typography>
          <Stack>
            {(field.options || []).map((opt) => {
              const current = Array.isArray(value) ? value : [];
              return (
                <FormControlLabel
                  key={opt}
                  control={
                    <Checkbox
                      checked={current.includes(opt)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          onChange([...current, opt]);
                        } else {
                          onChange(current.filter((v) => v !== opt));
                        }
                      }}
                    />
                  }
                  label={opt}
                />
              );
            })}
          </Stack>
          {hasError && <FormHelperText>{errorText}</FormHelperText>}
        </FormControl>
      );
    default:
      return null;
  }
}

function CustomFieldsGroup({
  fields,
  values,
  onValueChange,
  title,
  errorsByField,
  scope,
  participantKey,
  registerFieldNode,
}) {
  const sortedFields = [...fields].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  return (
    <Stack spacing={2}>
      {title && (
        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>
      )}
      {sortedFields.map((field) => (
        <Box
          key={field.fieldId}
          ref={(node) =>
            registerFieldNode(
              getFieldRefKey({
                scope,
                fieldId: field.fieldId,
                participantKey,
              }),
              node
            )
          }
        >
          {renderCustomField(
            field,
            values?.[field.fieldId],
            (val) => onValueChange(field.fieldId, val),
            errorsByField?.[field.fieldId] || ""
          )}
        </Box>
      ))}
    </Stack>
  );
}

export default function BookingStepCustomFields({
  event,
  participants,
  bookingCustomFieldValues,
  participantCustomFieldValues,
  onBookingFieldChange,
  onParticipantFieldChange,
  validationErrors,
  focusField,
  stepError,
}) {
  const customFields = event?.registrationConfig?.customFields || [];
  const bookingFields = customFields.filter((f) => f.scope === "booking");
  const participantFields = customFields.filter((f) => f.scope === "participant");
  const hasHealthSection = participantFields.some((f) => f.sensitive);
  const fieldRefs = useRef({});
  const [expandedParticipantKey, setExpandedParticipantKey] = useState(() =>
    participants.length > 0 ? getParticipantKey(participants[0], 0) : null
  );

  const bookingFieldErrors = validationErrors?.booking || {};
  const participantFieldErrors = validationErrors?.participants || {};

  const registerFieldNode = (refKey, node) => {
    if (node) {
      fieldRefs.current[refKey] = node;
    } else {
      delete fieldRefs.current[refKey];
    }
  };

  useEffect(() => {
    if (participants.length === 0) {
      setExpandedParticipantKey(null);
      return;
    }

    setExpandedParticipantKey((current) => {
      if (current && participants.some((p, idx) => getParticipantKey(p, idx) === current)) {
        return current;
      }
      return getParticipantKey(participants[0], 0);
    });
  }, [participants]);

  useEffect(() => {
    if (!focusField?.fieldId) return;

    const participantKey =
      focusField.scope === "participant"
        ? String(focusField.participantKey || "")
        : "";

    if (focusField.scope === "participant" && participantKey) {
      setExpandedParticipantKey(participantKey);
    }

    const refKey = getFieldRefKey({
      scope: focusField.scope,
      fieldId: focusField.fieldId,
      participantKey,
    });

    const focusAndScroll = () => {
      const node = fieldRefs.current[refKey];
      if (!node) return;

      node.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });

      const focusTarget =
        node.querySelector('input, textarea, [role="combobox"], button, [tabindex]') || node;
      if (typeof focusTarget.focus === "function") {
        focusTarget.focus({ preventScroll: true });
      }
    };

    const timer = window.setTimeout(
      focusAndScroll,
      focusField.scope === "participant" ? 80 : 0
    );
    return () => window.clearTimeout(timer);
  }, [focusField]);

  return (
    <Box>
      {stepError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {stepError}
        </Alert>
      )}

      {/* Booking-scoped Fields */}
      {bookingFields.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Angaben zur Buchung
          </Typography>
          <CustomFieldsGroup
            fields={bookingFields}
            values={bookingCustomFieldValues}
            onValueChange={onBookingFieldChange}
            errorsByField={bookingFieldErrors}
            scope="booking"
            participantKey=""
            registerFieldNode={registerFieldNode}
          />
        </Box>
      )}

      {/* Participant-scoped Fields (pro Teilnehmer) */}
      {participantFields.length > 0 && participants.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Angaben pro Teilnehmer
          </Typography>
          {participants.map((participant, idx) => {
            const minor = isMinor(participant.birthdate);
            const participantKey = getParticipantKey(participant, idx);
            const visibleFields = participantFields.filter((f) => {
              if (f.onlyForChildren && !minor) return false;
              return true;
            });

            return (
              <Accordion
                key={participantKey}
                expanded={expandedParticipantKey === participantKey}
                onChange={(_, expanded) =>
                  setExpandedParticipantKey(expanded ? participantKey : null)
                }
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<Icon icon="mdi:chevron-down" />}>
                  <Typography variant="body2" fontWeight={500}>
                    {participant.firstName} {participant.lastName}
                    {minor && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        (minderjährig)
                      </Typography>
                    )}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <CustomFieldsGroup
                    fields={visibleFields.filter((f) => !f.sensitive)}
                    values={participantCustomFieldValues?.[participantKey] || {}}
                    onValueChange={(fieldId, val) =>
                      onParticipantFieldChange(participantKey, fieldId, val)
                    }
                    errorsByField={participantFieldErrors?.[participantKey] || {}}
                    scope="participant"
                    participantKey={participantKey}
                    registerFieldNode={registerFieldNode}
                  />

                  {/* Gesundheitsdaten */}
                  {hasHealthSection && (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="info" icon={<Icon icon="mdi:shield-lock-outline" />} sx={{ mb: 2 }}>
                        Diese Angaben werden verschlüsselt gespeichert und sind nur für den Veranstaltungsleiter
                        einsehbar. Sie werden 30 Tage nach Veranstaltungsende automatisch gelöscht.
                      </Alert>
                      <Typography variant="subtitle2" gutterBottom>
                        Gesundheitsinformationen
                      </Typography>
                      <Stack spacing={1.5}>
                        <TextField
                          label="Allergien"
                          size="small"
                          fullWidth
                          multiline
                          rows={2}
                          value={participantCustomFieldValues?.[participantKey]?._healthAllergies || ""}
                          onChange={(e) =>
                            onParticipantFieldChange(participantKey, "_healthAllergies", e.target.value)
                          }
                        />
                        <TextField
                          label="Medikamente"
                          size="small"
                          fullWidth
                          value={participantCustomFieldValues?.[participantKey]?._healthMedications || ""}
                          onChange={(e) =>
                            onParticipantFieldChange(participantKey, "_healthMedications", e.target.value)
                          }
                        />
                        <TextField
                          label="Besondere Ernährungsanforderungen"
                          size="small"
                          fullWidth
                          value={participantCustomFieldValues?.[participantKey]?._healthDietary || ""}
                          onChange={(e) =>
                            onParticipantFieldChange(participantKey, "_healthDietary", e.target.value)
                          }
                        />
                        <Typography variant="subtitle2" sx={{ mt: 1 }}>
                          Notfallkontakt
                        </Typography>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                          <TextField
                            label="Name"
                            size="small"
                            fullWidth
                            value={participantCustomFieldValues?.[participantKey]?._emergencyName || ""}
                            onChange={(e) =>
                              onParticipantFieldChange(participantKey, "_emergencyName", e.target.value)
                            }
                          />
                          <TextField
                            label="Telefon"
                            size="small"
                            fullWidth
                            value={participantCustomFieldValues?.[participantKey]?._emergencyPhone || ""}
                            onChange={(e) =>
                              onParticipantFieldChange(participantKey, "_emergencyPhone", e.target.value)
                            }
                          />
                          <TextField
                            label="Beziehung (z.B. Mutter)"
                            size="small"
                            fullWidth
                            value={participantCustomFieldValues?.[participantKey]?._emergencyRelation || ""}
                            onChange={(e) =>
                              onParticipantFieldChange(participantKey, "_emergencyRelation", e.target.value)
                            }
                          />
                        </Stack>
                      </Stack>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}

      {bookingFields.length === 0 && participantFields.length === 0 && (
        <Typography color="text.secondary" variant="body2">
          Für diese Veranstaltung sind keine weiteren Angaben erforderlich.
        </Typography>
      )}
    </Box>
  );
}

BookingStepCustomFields.propTypes = {
  event: PropTypes.object.isRequired,
  participants: PropTypes.array.isRequired,
  bookingCustomFieldValues: PropTypes.object.isRequired,
  participantCustomFieldValues: PropTypes.object.isRequired,
  onBookingFieldChange: PropTypes.func.isRequired,
  onParticipantFieldChange: PropTypes.func.isRequired,
  validationErrors: PropTypes.shape({
    booking: PropTypes.object,
    participants: PropTypes.object,
  }),
  focusField: PropTypes.shape({
    scope: PropTypes.oneOf(["booking", "participant"]),
    participantKey: PropTypes.string,
    fieldId: PropTypes.string,
  }),
  stepError: PropTypes.string,
};

BookingStepCustomFields.defaultProps = {
  validationErrors: { booking: {}, participants: {} },
  focusField: null,
  stepError: "",
};
